import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CONTENT_CARD, GROUP, PAGE, SECTION_COLUMN } from 'constants/widgetTypes';

import { toggleEditMode, updateWidgetProperty, updateWidgetContent, actionGroup } from 'actions/editor';
import { EditorContext } from '../../index';

import { calculateBoundsForElement, getParentContainerOfType, isWidgetHasHotspotChild, getWidgetType } from 'components/Editor/helper/widget';
import { useHotspotContainerSelector, useAllowCreateLiveElement } from 'components/Editor/hooks/useEditorSession';
import { HotspotActionButton, HotspotCreationProperty } from '../../components/hotspot';

import _ from 'lodash';
import cx from 'classnames';
import { Property } from '../../components/properties/';

import WidgetActions from 'components/Editor/components/properties/widgetActions';
import StoryboardContext from 'components/Storyboard/context';
import { MediaLibraryModal } from 'components/MediaLibrary';
import { LiveElementCreationProperty } from '../../components/liveElements';
import { Animation, AnimationProperty} from 'components/Editor/components/animations';

import styles from './video.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const VideoWidgetComponent = function ({ className, style, widgetProps, widgetRef }) {
  const dispatch = useDispatch();

  const videoRef = React.useRef(null);

  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const content = _.get(widgetProps, 'content');

  return (
    <>
      <div id={id}
        ref={ref}
        className={cx(className, styles.video, 'widget')}
        style={style}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={true}
      >
        <Animation widget={widgetProps}>
          <VideoView url={content ? content.url : ''} id={id}
            videoRef={videoRef} />
        </Animation>
      </div>
    </>
  )
}

const videoWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const VideoWidget = React.memo(VideoWidgetComponent, videoWidgetEquals);

const VideoView = ({ videoRef, url, width, height, crop, breakpoint, id }) => {
  const style = {
    width: width
  }

  useEffect(() => {
    if (videoRef) {
      videoRef.current?.load();
    }
  }, [url])

  return (
    <video ref={videoRef} name="media" className={styles.videoPlayer} muted
      style={style}>
      <source src={`${IMAGE_CDN_ENDPOINT}/${url}`} />
    </video>
  );
}

const VideoWidgetDeskptopAction = (props) => {
  const dispatch = useDispatch();
  const editorContext = React.useContext(EditorContext);

  const entityType = _.get(editorContext, 'entityType', null);

  const isHotspotContainer = useHotspotContainerSelector(props.editorSession, props);
  const allowLiveElement = useAllowCreateLiveElement(props.editorSession, props);

  const [state,setState] = useState({
    view: null,
    library:false,
    animation:false
  })

  const storyboardContext = React.useContext(StoryboardContext);
  const id = _.get(props, 'uid', null);
  const template = _.get(props, 'template.widget') != null;

  const toggleImageLibrary = () => {
    setState({
      library: true,
      animation: false
    })
  }

  const changeView = () => {
    setState({
      library: false,
      animation: true
    })
  }

  const handleUpdateVideo = (media) => {
    setState(state => {
      return {
        ...state,
        library: false,
      }
    })

    const naturalWidth = _.get(media, 'width') || 600;
    const naturalHeight = _.get(media, 'height') || 600;

    var desktopWidth = Math.min(600, naturalWidth);
    var desktopHeight = desktopWidth * naturalHeight / naturalWidth;

    var mobileWidth = Math.max(300, naturalWidth);
    var mobileHeight = mobileWidth * naturalHeight / naturalWidth;

    actionGroup.start();

    dispatch(updateWidgetContent(props.editorSession, props.uid, media[0]));

    const desktopLayoutProperty = _.get(props, 'properties.desktop.layout');
    const mobileLayoutProperty = _.get(props, 'properties.mobile.layout');

    dispatch(updateWidgetProperty(props.editorSession, props.uid, 'desktop', {
      layout: {
        ...desktopLayoutProperty,
        width: desktopWidth,
        height: desktopHeight
      }
    }));

    dispatch(updateWidgetProperty(props.editorSession, props.uid, 'mobile', {
      layout: {
        ...mobileLayoutProperty,
        width: mobileWidth,
        height: mobileHeight
      }
    }));

    actionGroup.end();
  }

  const handleLiveElement = () => {
    setState(state => {
      return {
        ...state,
        view: 'liveElements'
      }
    })
  }

  const handleHotspot = () => {
    setState(state => {
      return {
        ...state,
        view: 'hotspot'
      }
    })
  }

  const closePopup = () => {
    dispatch(toggleEditMode(props.editorSession, props.uid, false));

    setState(state => {
      return {
        ...state,
        view: null
      }
    });
  }

  return (
    <>
      <WidgetActions.Action className={styles.textButton} onClick={toggleImageLibrary}>Change Video</WidgetActions.Action>
      <WidgetActions.Divider />
      <Property.Action className={styles.iconButton} tooltip="Animation" onClick={() => changeView()}>
        <i className={'cleverstory-animations'} />
      </Property.Action>
      {!template && allowLiveElement &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }

      {!template && !isHotspotContainer &&
        <HotspotActionButton onClick={handleHotspot} widget={props} />
      }
      <WidgetActions.Divider />
      <WidgetActions.Action className={styles.iconButton} tooltip="Help" onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </WidgetActions.Action>

      {state.view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {state.view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }

      <MediaLibraryModal
        show={state.library}
        storyboard={storyboardContext}
        mediaTypes={['video']}
        onSelect={handleUpdateVideo}
        onClose={() => setState(state => {
          return {
            ...state,
            library: false,
          }
        })}
        target= {entityType === "live_element" ? {
            id: editorContext.editorSession.id.split('_')[1],
            type: 'live_element'
          } : null 
        }
      />

      {!state.library && state.animation &&
        <AnimationProperty
          id={props.uid}
          editor={props.editorSession}
          editorContext={editorContext}
          widget={props}
          onCancel={() => {
            setState(state => {
              return {
                ...state,
                animation: false,
              }
            })
          }}
        />
      }
    </>
  )
}

const VideoWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState('style')

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const isHotspotContainer = useHotspotContainerSelector(props.editorSession, props);
  const template = _.get(props, 'template.widget') != null;

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
        <i className={'cleverstory-animations'} />
      </Property.Action>

      {!template && !isHotspotContainer &&
        <HotspotActionButton onClick={() => changeView('hotspot')} widget={props} />
      }

      <Property.Action onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }} tooltip='Help' className={styles.iconButton}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {!editMode && view === 'animation' &&
        <AnimationProperty
          id={id}
          editor={editorSession}
          editorContext={editorContext}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }
    </>
  );
}

VideoWidget.moveableConfig = (editorSession, widget) => {
  const contentCard = getParentContainerOfType(editorSession, widget.uid, CONTENT_CARD);
  var bounds = null;
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }

  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    bounds: bounds,
    renderDirections: ["se", "sw", "ne", "nw"],
    onReparent: (widget, parent) => {
      const parentWidget = getParentContainerOfType(editorSession, widget, CONTENT_CARD);

      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);

      const denyHostpotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN].includes(parentType);

      if (parentWidget || group || denyHostpotReparent) {
        return false;
      }

      return true;
    }
  }
}

VideoWidget.actions = (editorSession, breakpoint, props) => {
  if (breakpoint === 'desktop') {
    return (
      <VideoWidgetDeskptopAction
        editorSession={editorSession} {...props}
      />
    );
  }

  if (breakpoint === 'mobile') {
    return (
      <VideoWidgetMobileAction
        editorSession={editorSession} {...props}
      />
    );
  }

  return false;
}

VideoWidget.processRequest = (widget) => {
  var content = _.get(widget, 'content.id');
  return {
    ...widget,
    content: content
  }
}

export { VideoWidget };